import React from 'react';
import PropTypes from 'prop-types';
import './StatusTimeline.css';

// `milestone` is the MES status code the backend keys that step's date by. It is
// spelled out because `code` here is the display/matching code, and several of
// those (1001, 3001, 4001, 5001, 6001) are placeholders Mailman never sends —
// steps match by label today. The real progression is 2001 / 1003 / 1005 / 1007
// / 1009. Steps Mailman publishes no event for carry no milestone.
const mailmanStatusFlow = [
  { code: 'Waiting for clinician to assign', label: 'Waiting for clinician to assign' },
  { code: '1001', label: 'Order Placed' },
  { code: '2001', label: 'Order Processing', milestone: '2001' },
  { code: '3001', label: 'In-Transit to Patient', milestone: '1003' },
  { code: '4001', label: 'In-Transit to MES', milestone: '1005' },
  { code: '5001', label: 'Test in Process', milestone: '1007' },
  { code: '6001', label: 'Test Complete', milestone: '1009' },
];

// Tasso's status codes are the milestone keys as-is, so no `milestone` override
// is needed here.
const tassoStatusFlow = [
  { code: 'accepted', label: 'Order Accepted' },
  { code: 'pendingfulfillment', label: 'At Tasso' },
  { code: 'inlogistics', label: 'In logistics' },
  { code: 'readytoship', label: 'Ready to Ship' },
  // Tasso's own step between "ready to ship" and "at patient". It was missing
  // here, so an order sitting in it matched no step and the whole Hormone
  // timeline rendered with nothing marked current.
  { code: 'intransittopatient', label: 'In-Transit to Patient' },
  { code: 'atpatient', label: 'At Patient' },
  { code: 'intransittolab', label: 'In Transit To Lab' },
  { code: 'atlab', label: 'At Lab' },
  { code: 'resultsready', label: 'Results Ready' },
  { code: 'replaced', label: 'Replaced' },
];

const formatTimelineDate = (value) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * When each step actually happened, keyed by lab status code.
 *
 * The backend sends `milestones` on both legs — the lab's own dated history
 * (Tasso `/orderEvents`, Mailman `statusTimeLine`), one entry per status
 * reached. Steps with no entry report no date rather than borrowing one.
 *
 * `updated_at` is the Order row's own timestamp: it moves every time anything on
 * the order is edited, so it is *not* a step date. It stays as the fallback only
 * for clients talking to a backend that predates this field.
 */
const stepDates = (statusForType) => (typeof statusForType === 'object' && statusForType?.milestones) || null;

const StatusTimeline = ({ currentStatus, type = 'mailman', intake = null }) => {
  const types = Array.isArray(type) ? type : [type];
  console.log('currentStatus', currentStatus);

  // Intake is order-level (not per-kit), so it renders as its own row above the
  // kit timelines instead of being duplicated into each one.
  const renderIntakeTimeline = () => {
    const steps = [
      { code: 'intake_email_sent', label: 'Intake Email Sent', done: !!intake?.emailSent, at: intake?.emailSentAt },
      { code: 'intake_submitted', label: 'Intake Submitted', done: !!intake?.submitted, at: intake?.submittedAt },
    ];
    // A submitted intake implies the email went out, even on legacy orders with no flag.
    if (steps[1].done) steps[0].done = true;
    const lastDoneIndex = steps.reduce((last, step, index) => (step.done ? index : last), -1);

    return (
      <div className="timeline" key="intake">
        <h4 className="timeline-title">Intake Form</h4>
        {steps.map((step, index) => (
          <div key={step.code} className={`timeline-item ${step.done ? 'completed' : ''} ${index === lastDoneIndex ? 'current' : ''}`}>
            <div className="timeline-icon"></div>
            <div className="timeline-content">
              <div className="status-label">{step.label}</div>
              {step.done && step.at && <div className="status-date">{formatTimelineDate(step.at)}</div>}
              {!step.done && <div className="status-date">Pending</div>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTimeline = (timelineType) => {
    const statusFlow = timelineType === 'tasso' ? tassoStatusFlow : mailmanStatusFlow;

    const statusForType = typeof currentStatus === 'object' && currentStatus !== null ? currentStatus[timelineType] : currentStatus;

    console.log(`timelineType: ${timelineType}, statusForType:`, statusForType);

    const statusCode = (() => {
      if (!statusForType) return '';
      if (typeof statusForType === 'string') return String(statusForType).toLowerCase().trim();
      if (typeof statusForType === 'number') return String(statusForType);
      if (typeof statusForType === 'object' && statusForType.code) {
        return String(statusForType.code).toLowerCase().trim();
      }
      return '';
    })();

    const statusLabel = (() => {
      if (!statusForType) return '';
      if (typeof statusForType === 'object' && statusForType.label) {
        return String(statusForType.label).toLowerCase().trim();
      }
      return '';
    })();

    const defaultStatus = timelineType === 'tasso' ? 'accepted' : 'Waiting for clinician to assign';

    console.log(`statusCode: "${statusCode}", statusLabel: "${statusLabel}", defaultStatus: "${defaultStatus}"`);

    const currentStatusIndex = statusFlow.findIndex(
      (status) =>
        String(status.code).toLowerCase() === String(statusCode || defaultStatus).toLowerCase() ||
        String(status.label).toLowerCase() === String(statusLabel || defaultStatus).toLowerCase()
    );

    console.log(`currentStatusIndex: ${currentStatusIndex} for ${timelineType}`);

    const milestones = stepDates(statusForType);

    return (
      <div className="timeline" key={timelineType}>
        <h4 className="timeline-title">{timelineType === 'tasso' ? 'Hormone Panel Kit' : 'Semen Analysis Kit'}</h4>
        {statusFlow.map((status, index) => {
          const isDefaultCompleted = index === 0;
          const isCompleted = isDefaultCompleted || (index <= currentStatusIndex && currentStatusIndex !== -1);
          const isCurrent = index === currentStatusIndex;

          // With a milestone history, every step reached shows the date the lab
          // reported it. Without one, fall back to the old behaviour: the
          // order's updated_at on the current step only.
          const occurredAt = milestones ? milestones[status.milestone ?? status.code] : isCurrent ? statusForType?.updated_at : null;

          return (
            <div key={status.code} className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
              <div className="timeline-icon"></div>
              <div className="timeline-content">
                <div className="status-label">{status.label}</div>
                {occurredAt && <div className="status-date">{formatTimelineDate(occurredAt)}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="status-timeline">
      <h3>Order Status</h3>
      {intake && renderIntakeTimeline()}
      {types.map((timelineType) => renderTimeline(timelineType))}
    </div>
  );
};

StatusTimeline.propTypes = {
  currentStatus: PropTypes.oneOfType([
    PropTypes.shape({
      code: PropTypes.string,
      label: PropTypes.string,
      updated_at: PropTypes.string,
      status_changed_at: PropTypes.string,
      // { lab status code: ISO timestamp } — the lab's dated step history.
      milestones: PropTypes.objectOf(PropTypes.string),
    }),
    PropTypes.string,
  ]),
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  intake: PropTypes.shape({
    emailSent: PropTypes.bool,
    emailSentAt: PropTypes.string,
    submitted: PropTypes.bool,
    submittedAt: PropTypes.string,
  }),
};

export default StatusTimeline;
